import { useState } from "react";
import axios from "axios";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SUPPORT_EMAIL } from "@/config";

// Relative path so the same build works on any host (ingress/nginx proxies /api to the backend).
const API = "/api";

const FIELD_CLS =
  "w-full rounded-md border border-line bg-surface2 px-4 py-3 text-sm text-onsurface placeholder:text-onsurface2/60 focus:border-brand-light focus:outline-none focus:ring-1 focus:ring-brand-light";

const SupportPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="support-page" className="mx-auto max-w-3xl px-4 pb-24 pt-32 sm:px-6">
      <p className="font-display text-sm font-semibold uppercase tracking-widest text-brand-light">Support</p>
      <h1 className="mt-3 font-display text-4xl font-bold uppercase tracking-wide text-onsurface">
        How can we help?
      </h1>
      <p className="mt-4 text-sm text-onsurface2">
        Send us a message below, or email us directly at{" "}
        <a data-testid="support-mailto-link" href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-1 text-brand-light hover:underline">
          <Mail className="h-3.5 w-3.5" /> {SUPPORT_EMAIL}
        </a>
      </p>

      <form data-testid="support-form" onSubmit={handleSubmit} className="mt-10 space-y-5 rounded-lg border border-line bg-surface2/50 p-6 sm:p-8">
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="support-name" className="mb-2 block font-display text-xs font-semibold uppercase tracking-widest text-onsurface2">Name</label>
            <input
              id="support-name"
              data-testid="support-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className={FIELD_CLS}
            />
          </div>
          <div>
            <label htmlFor="support-email" className="mb-2 block font-display text-xs font-semibold uppercase tracking-widest text-onsurface2">Email</label>
            <input
              id="support-email"
              data-testid="support-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              className={FIELD_CLS}
            />
          </div>
        </div>
        <div>
          <label htmlFor="support-message" className="mb-2 block font-display text-xs font-semibold uppercase tracking-widest text-onsurface2">Message</label>
          <textarea
            id="support-message"
            data-testid="support-message-input"
            required
            rows={5}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Tell us what's going on…"
            className={FIELD_CLS}
          />
        </div>
        <button
          data-testid="support-submit-btn"
          type="submit"
          disabled={sending}
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-8 py-3 font-display text-sm font-bold uppercase tracking-wider text-white shadow-cta transition-shadow duration-200 hover:shadow-cta-lg disabled:opacity-60"
        >
          {sending && <Loader2 className="h-4 w-4 animate-spin" />}
          {sending ? "Sending…" : "Send message"}
        </button>
      </form>
    </main>
  );
};

export default SupportPage;
